import type { CampaignPoint } from "../api";
import { duration, full, pct } from "./format";

/**
 * Every campaign in the window, busiest first.
 *
 * Busiest rather than alphabetical: a customer with eight campaigns has one
 * or two that matter and a long tail, and sorting by name buries the ones
 * worth looking at under the ones that ran twice.
 *
 * The table is also the accessible view of the charts above — the same
 * numbers, readable without colour or hover.
 */
export default function CampaignTable({
  campaigns,
  selected,
  onSelect,
}: {
  campaigns: CampaignPoint[];
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  if (campaigns.length === 0) return null;

  // Every campaign bucketed the same way is the normal case and needs no
  // explaining; a mix does, because two rows then mean different days.
  const zones = [...new Set(campaigns.map((c) => c.bucket_tz))];

  return (
    <div className="ctable">
      <table>
        <thead>
          <tr>
            <th>Campaign</th>
            <th className="n">Leads</th>
            <th className="n">Attempts</th>
            <th className="n">Connected</th>
            <th className="n">Connect</th>
            <th className="n">Att/lead</th>
            <th className="n" title="Total call length of connected calls — voicemail excluded">
              Conn min
            </th>
            <th className="n">Avg talk</th>
            <th className="n">Failed</th>
          </tr>
        </thead>
        <tbody>
          {campaigns.map((c) => (
            <tr
              key={c.campaign_id}
              className={c.campaign_id === selected ? "on" : ""}
              onClick={() => onSelect(c.campaign_id)}
            >
              <td>
                {/* The mirror may not have seen this campaign yet — a
                    rollup can land before the directory sync does. Showing
                    the id beats hiding the row. */}
                <span className="cid">{c.identifier ?? c.campaign_id}</span>
                {zones.length > 1 && <span className="tz">{c.bucket_tz}</span>}
              </td>
              <td className="n">{full(c.leads_created)}</td>
              <td className="n">{full(c.attempts)}</td>
              <td className="n">{full(c.connected)}</td>
              <td className="n">{pct(c.connect_rate)}</td>
              <td className="n">{c.attempts_per_lead?.toFixed(2) ?? "—"}</td>
              <td className="n">{full(Math.round(c.talk_seconds / 60))}</td>
              <td className="n">{duration(c.avg_talk_seconds)}</td>
              <td className={c.trigger_failed > 0 ? "n bad" : "n"}>
                {full(c.trigger_failed)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
